import { useQuery } from '@tanstack/react-query';
import { Checkbox } from '../../components/ui/checkbox';
import { DatePicker, formatDateLabel } from '../../components/ui/DatePicker';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { SelectField } from '../../components/ui/SelectField';
import { TokenChipInput } from '../../components/ui/TokenChipInput';
import { normaliseTag } from '../../lib/portalLibrary/entryRules';
import {
  PORTAL_SHARED_LINK_NOTE,
  portalFieldsForSection,
  sectionAudienceHint,
  type PortalPublishFields,
} from '../../lib/portalLibrary/staffView';
import type { Account } from '../../types/account';
import {
  PORTAL_LIBRARY_SECTION_LABELS,
  PORTAL_LIBRARY_SECTIONS,
  type PortalLibrarySection,
} from '../../types/portalLibrary';
import { listPortalLibraryTags } from './api';
import { AudienceEditor } from './AudienceEditor';
import { CategorySelect } from './CategoryControls';

// The "Add to Kyck portal" fields on the hosted-report upload form.
//
// ⚠️⚠️ ONE DATE, NOT TWO (brief §3c). While these fields are showing, the upload
// form's own expiry control is REMOVED — not disabled, not synced — and the
// link's expiry is this end date. Two controls showing one truth is how the
// second one comes back.

const SECTION_OPTIONS = PORTAL_LIBRARY_SECTIONS.map((s) => ({
  value: s,
  label: PORTAL_LIBRARY_SECTION_LABELS[s],
}));

export function PortalPublishSection({
  value,
  onChange,
  accounts,
}: {
  value: PortalPublishFields;
  onChange: (v: PortalPublishFields) => void;
  accounts: Account[];
}) {
  const tagsQuery = useQuery({
    queryKey: ['portal-library-tags', value.section],
    queryFn: () => listPortalLibraryTags(value.section),
  });
  const suggestions = (tagsQuery.data ?? []).filter((t) => !value.tags.includes(t));

  return (
    <div className="space-y-4 rounded-md border border-slate-200 p-3">
      <div className="space-y-1.5">
        <Label htmlFor="pl-section">Section</Label>
        <SelectField
          id="pl-section"
          value={value.section}
          // Choosing a section resets the end date to THAT section's default.
          onValueChange={(s) =>
            onChange(portalFieldsForSection(s as PortalLibrarySection, Date.now(), value))
          }
          options={SECTION_OPTIONS}
          aria-label="Section"
        />
        {/* ⚠️ Round 6 §3: a strategy section is shown to only one side of the
            franchise / everyone-else split — said before the audience is chosen,
            and enforced by the reach line's refusal below. */}
        {sectionAudienceHint(value.section) && (
          <p className="text-xs text-slate-500">{sectionAudienceHint(value.section)}</p>
        )}
      </div>

      {value.section === 'strategy.monthly' && (
        <div className="space-y-1.5">
          <Label htmlFor="pl-period">Month</Label>
          <Input
            id="pl-period"
            type="month"
            value={value.period}
            onChange={(e) => onChange({ ...value, period: e.target.value })}
          />
          <p className="text-xs text-slate-500">Monthly campaigns are listed by month.</p>
        </div>
      )}

      {/* Round 4 §6: exactly one category for a Kyck college item. */}
      {value.section === 'college' && (
        <div className="space-y-1.5">
          <Label htmlFor="pl-category">Category</Label>
          <CategorySelect
            id="pl-category"
            value={value.categoryId}
            onChange={(categoryId) => onChange({ ...value, categoryId })}
          />
          <p className="text-xs text-slate-500">
            The filter clients see along the top of Kyck college.
          </p>
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="pl-summary">Summary (optional)</Label>
        <Input
          id="pl-summary"
          value={value.summary}
          maxLength={280}
          onChange={(e) => onChange({ ...value, summary: e.target.value })}
          placeholder="One or two lines under the title"
        />
      </div>

      <div className="space-y-1.5">
        <Label>Who sees it</Label>
        <AudienceEditor
          value={value.audience}
          onChange={(audience) => onChange({ ...value, audience })}
          accounts={accounts}
          section={value.section}
        />
      </div>

      <div className="space-y-1.5">
        <Label>Tags (optional)</Label>
        <TokenChipInput
          value={value.tags}
          onChange={(tags) =>
            onChange({ ...value, tags: [...new Set(tags.map(normaliseTag).filter(Boolean))] })
          }
          placeholder="Type a tag and press Enter"
          ariaLabel="Tags"
        />
        {suggestions.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {suggestions.slice(0, 12).map((t) => (
              <button
                key={t}
                type="button"
                className="rounded-full border border-slate-200 px-2 py-0.5 text-xs text-slate-600 hover:bg-slate-50"
                onClick={() => onChange({ ...value, tags: [...value.tags, t] })}
              >
                + {t}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="pl-from">Show from</Label>
          <DatePicker
            id="pl-from"
            value={value.visibleFromDate || null}
            onChange={(iso) => onChange({ ...value, visibleFromDate: iso })}
            ariaLabel="Show from"
          />
          <p className="text-xs text-slate-500">
            {value.visibleFromDate
              ? `Appears on ${formatDateLabel(value.visibleFromDate)}.`
              : 'Appears as soon as it’s published.'}
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="pl-until">Show until</Label>
          {!value.neverEnds && (
            <DatePicker
              id="pl-until"
              value={value.visibleUntilDate}
              onChange={(iso) => onChange({ ...value, visibleUntilDate: iso })}
              ariaLabel="Show until"
            />
          )}
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <Checkbox
              checked={value.neverEnds}
              onCheckedChange={(v) => onChange({ ...value, neverEnds: v === true })}
              aria-label="Never comes down"
            />
            Never comes down
          </label>
          <p className="text-xs text-slate-500">
            {value.neverEnds
              ? 'Stays on the portal, and the link keeps working, until someone removes it.'
              : `Comes down after ${formatDateLabel(value.visibleUntilDate)} — and the link stops working then too.`}
          </p>
        </div>
      </div>
      {/* ⚠️ Round 4 §1: said on BOTH doors (this section is rendered by the
          upload form AND the adopt dialog). */}
      <p className="text-xs text-slate-600" data-testid="portal-shared-link-note">
        {PORTAL_SHARED_LINK_NOTE}
      </p>
    </div>
  );
}
